import React, {useState} from 'react';
import {Checkbox, ProjectIndicator, TaskRow as TaskRowComponent, TaskRowV2 as TaskRowComponentV2} from 'web-components';
import {createFragmentContainer, graphql} from 'react-relay';
import {FavouredActionWrapper} from '../../../hooks/favoured_action_wrapper';
import {assigneesElement, getDateText, getEstimatePoints, getEstimateHours, getHoursRegistered, getProgressPercentage, getRemainingHours, getRemainingPoints, ownerElement, statusElement} from './task_row_elements_logic';
import GenericTaskContextMenu from '../../context-menus/generic_task_context_menu';
import TimeRegPopup from '../../popups/time_reg_popup';
import {hasFeatureFlag} from '../../../util/FeatureUtil';
import {ESTIMATION_UNIT} from "../../../../../constants";
import {hasPermission} from "../../../util/PermissionsUtil";
import {PERMISSION_TYPE} from "../../../../../Permissions";
import {ProgressRegistrationPopup} from "../../popups/ProgressRegistrationPopup";
import Util from "../../../util/util";

const TaskRow = ({intl, task, enabledColumns, showTaskModal, viewer, cy}) => {
	const [selected, setSelected] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState();

	const onCheckHandler = c => {
		setSelected(!selected);
	};

	const openCardModal = (e, companyTaskId) => {
		e.stopPropagation();
		e.preventDefault();
		if (showTaskModal) {
			showTaskModal(companyTaskId);
		}
	};

	const openContextMenu = e => {
		e.preventDefault();
		const contextMenuPosition = {};
		const btn = document.elementFromPoint(e.pageX, e.pageY);
		if (btn != null && e.type !== 'contextmenu') {
			const btnBounds = btn.getBoundingClientRect();
			contextMenuPosition.x = btnBounds.right - btn.clientWidth - 2;
			if (window.innerHeight - btnBounds.bottom < 250) {
				contextMenuPosition.y = btnBounds.bottom - 282;
			} else {
				contextMenuPosition.y = btnBounds.bottom + 2;
			}
		} else {
			contextMenuPosition.x = e.pageX;
			//check if there is place for menu underneath cursor
			if (window.innerHeight - e.pageY < 250) {
				contextMenuPosition.y = e.pageY - 250;
			} else {
				contextMenuPosition.y = e.pageY;
			}
		}
		setContextMenuPosition(contextMenuPosition);
	};

	const closeContextMenu = () => {
		setContextMenuPosition(null);
	};

	const isEstimatedInPoints = task.project.estimationUnit === ESTIMATION_UNIT.POINTS;

	// Parent task name
	const useTaskHierarchy = hasFeatureFlag('task_hierarchy', viewer.availableFeatureFlags) && hasFeatureFlag('task_hierarchy_part2', viewer.availableFeatureFlags);
	const parentTaskName = useTaskHierarchy && task.project.useTaskHierarchy && task.parentTask?.name;

	// This is handling two versions of the TaskRow / TaskHeader for supporting the new Sub Task with a feature flag
	// PLEASE CLEAN THIS UP AFTERWARDS.
	const MyTaskRowComponent = useTaskHierarchy ?
		TaskRowComponentV2 :
		TaskRowComponent;

	const noTimeReg = hasFeatureFlag('no_time_reg');

	return (
		<MyTaskRowComponent
			cy={cy}
			checkboxIcon={() => <Checkbox checked={selected} onClick={c => onCheckHandler(c)} />}
			taskId={task.companyTaskId}
			taskName={task.name}
			isFavoured={task.favoured}
			starIcon={({isFavoured}) => <FavouredActionWrapper isFavoured={isFavoured} taskId={task.id} />}
			projectIndicatorIcon={<ProjectIndicator project={task.project} />}
			projectName={task.project.name}
			clientName={task.project.client && task.project.client.name}
			columns={enabledColumns}
			isSelected={selected}
			openCardModal={e => openCardModal(e, task.companyTaskId)}
			assigneesElement={assigneesElement(task)}
			ownerElement={ownerElement(task)}
			statusElement={statusElement(task, intl)}
			startDate={getDateText(task.startDay, task.startMonth, task.startYear, intl)}
			deadline={getDateText(task.deadlineDay, task.deadlineMonth, task.deadlineYear, intl)}
			milestone={task.phase ? task.phase.name : null}
			estimate={hasPermission(PERMISSION_TYPE.CLIENT_HIDE_ESTIMATES) ?
				'-' : isEstimatedInPoints ?
					getEstimatePoints(task, intl) : getEstimateHours(task, intl)}
			timeEntries={hasPermission(PERMISSION_TYPE.CLIENT_HIDE_TIME_ENTRIES) ? '-' : getHoursRegistered(task, intl)}
			remaining={(hasPermission(PERMISSION_TYPE.CLIENT_HIDE_ESTIMATES) || hasPermission(PERMISSION_TYPE.CLIENT_HIDE_TIME_ENTRIES)) ?
				'-' : isEstimatedInPoints ? getRemainingPoints(task, intl) : getRemainingHours(task, intl)}
			labels={task.taskLabels.map(entry => entry.label)} //The actual label is stored in the label field under taskLabels
			progress={isEstimatedInPoints ? Util.getSimpleProgressPercentageFromTask(task) + '%' : getProgressPercentage(task, intl)}
			onContextMenu={e => openContextMenu(e)}
			contextMenuElement={() =>
				contextMenuPosition ? (
					<GenericTaskContextMenu cy="new-ui-favourite-tasks-context-menu-v2" task={task} viewer={viewer} contextMenuPosition={contextMenuPosition} closeContextMenu={() => closeContextMenu()}></GenericTaskContextMenu>
				) : null
			}
			isBug={task.bug}
			isHighPriority={task.highPriority}
			isBlocked={task.blocked}
			timeRegPopup={<TimeRegPopup viewer={viewer} projectId={task.project.id} task={task} harvestTask={false} />}
			parentTaskName={parentTaskName}
			intl={intl}
			noTimeReg={noTimeReg}
			progressRegistrationPopup={<ProgressRegistrationPopup task={task} />}
		/>
	);
};

export default createFragmentContainer(TaskRow, {
	task: graphql`
		fragment TaskRow_task on Task {
			id
			name
			companyTaskId
			startYear
			startMonth
			startDay
			deadlineDay
			deadlineMonth
			deadlineYear
			favoured
			estimateForecast
			timeLeft
			progress
			blocked
			highPriority
			userCanDeleteTask
			userCantDeleteTaskReason
			bug
			parentTaskId
			timeRegistrations(first: 1000000) @connection(key: "Task_timeRegistrations", filters: []) {
				edges {
					node {
						id
						minutesRegistered
					}
				}
			}
			assignedPersons {
				id
				firstName
				lastName
				fullName
				profilePictureId
				profilePictureDefaultId
				active
				permissions
				role {
					id
					name
				}
			}
			role {
				name
			}
			owner {
				id
				firstName
				lastName
				fullName
				profilePictureId
				profilePictureDefaultId
				role {
					id
					name
				}
			}
			phase {
				id
				name
			}
			statusColumnV2 {
				id
				name
				category
			}
			project {
				id
				name
				estimationUnit
				projectColor
				companyProjectId
				fullAccessToProject
				status
                useTaskHierarchy
				harvestProject {
					id
				}
				unit4Project {
					id
					activitiesEnabled
				}
				client {
					id
					name
				}
				statusColumnsV2(first: 1000000) {
					edges {
						node {
							id
							name
							order
						}
					}
				}
			}
			assignedPersons {
				id
				firstName
				lastName
				fullName
				profilePictureId
				profilePictureDefaultId
				active
				permissions
				role {
					id
					name
				}
			}
			taskLabels {
				id
				label {
					id
					name
					color
				}
			}
			parentTask {
				id
				name
			}
		}
	`,
});
